import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import {
    AnnotationType,
    emptyPrompt,
    genEntityId,
    InteractionType,
    IPrompt,
    IQuestion,
    isCustomizableSection,
    Perspective,
    PromptId,
    QuestionCategories,
} from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { DeleteButton } from "components/ui/action-button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { SecondaryButton } from "components/ui/secondary-button";
import * as React from "react";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- generic constraint
type PromptUpdateCallback = <T extends {}>(
    cb: (q: IPrompt, val: T) => void,
) => (val: T) => void;

interface Props {
    question: IQuestion;
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- generic constraint
    updateQuestion: <T extends {}>(
        cb: (q: IQuestion, val: T) => void,
    ) => (val: T) => void;
}

export const EditOptions: React.FunctionComponent<Props> = props => {
    const sortedPrompts = props.question.prompts
        .slice()
        .sort((a, b) => a.order - b.order);
    const prompts = sortedPrompts.map((p, i) => {
        const first = i === 0;
        const last = i === sortedPrompts.length - 1;
        const updatePrompt = buildUpdatePrompt(props, p);
        const deleteAction = () => {
            props.updateQuestion(
                q => (q.prompts = q.prompts.filter(prompt => prompt.id !== p.id)),
            )(null);
        };
        const moveUp = first
            ? null
            : () => {
                  const order = Math.floor(
                      i === 1
                          ? sortedPrompts[0].order - 1000
                          : (sortedPrompts[i - 2].order + sortedPrompts[i - 1].order) / 2,
                  );
                  updatePrompt(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                      (newPrompt, newOrder: any) => (newPrompt.order = newOrder),
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                  )(order as any);
              };
        const moveDown = last
            ? null
            : () => {
                  const order = Math.floor(
                      i === sortedPrompts.length - 2
                          ? sortedPrompts[i + 1].order + 1000
                          : (sortedPrompts[i + 1].order + sortedPrompts[i + 2].order) / 2,
                  );
                  updatePrompt(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                      (newPrompt, newOrder: any) => (newPrompt.order = newOrder),
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                  )(order as any);
              };
        return (
            <EditOption
                key={p.id}
                question={props.question}
                moveUp={moveUp}
                moveDown={moveDown}
                delete={deleteAction}
                updatePrompt={updatePrompt}
                prompt={p}
            />
        );
    });

    const addOption = props.updateQuestion((q: IQuestion) => {
        const order = q.prompts.reduce(
            (highest: number, prompt: IPrompt) => Math.max(highest, prompt.order) + 1,
            0,
        );
        const prompt = emptyPrompt(order, genEntityId<PromptId>());
        prompt.legacyClientAnnotation = q.legacyClientAnnotation !== AnnotationType.none;
        prompt.legacyCommunityAnnotation =
            q.legacyCommunityAnnotation !== AnnotationType.none;
        q.prompts.push(prompt);
    });

    return (
        <InputFormBlock additionalClassName="flex-1">
            <h2 className="text-lg font-medium twoverride">Multiple Choice Options</h2>
            <div className="flex py-2 border-b border-gray-300">
                <div className="flex-1 font-medium">Client label for option</div>
                <div className="flex-1 font-medium">Community label for option</div>
                <div className="flex-1 font-medium">Reorder / Remove</div>
            </div>
            {prompts}
            <div className="flex mt-4">
                <div className="flex-1"></div>
                <div className="flex-1"></div>
                <div className="flex-1">
                    <SecondaryButton type="button" onClick={addOption}>
                        Add new option
                    </SecondaryButton>
                </div>
            </div>
        </InputFormBlock>
    );
};

interface EditOptionProps {
    question: IQuestion;
    prompt: IPrompt;
    updatePrompt: PromptUpdateCallback;
    moveUp?: () => void;
    moveDown?: () => void;
    delete: () => void;
}

const EditOption: React.FunctionComponent<EditOptionProps> = props => {
    const additionalNotesLabel = "Allow additional notes";
    return (
        <div className="flex py-2 px-2" data-prompt={props.prompt.id}>
            <div className="flex-1 pr-2">
                {props.question.legacyClientInteraction !== InteractionType.none &&
                    isCustomizableSection(
                        Perspective.Client,
                        props.question.category as QuestionCategories,
                    ) && (
                        <>
                            <Input
                                value={props.prompt.legacyClientTitle}
                                onChange={props.updatePrompt(
                                    (prompt, val) =>
                                        (prompt.legacyClientTitle = val.target.value),
                                )}
                            />
                            {props.question.legacyClientAnnotation !==
                                AnnotationType.none && (
                                <div className="mt-1">
                                    <Checkbox
                                        label={additionalNotesLabel}
                                        checked={props.prompt.legacyClientAnnotation}
                                        onChange={e =>
                                            props.updatePrompt(
                                                (prompt, val) =>
                                                    (prompt.legacyClientAnnotation =
                                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                                                        val as any),
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                                            )(e.target.checked as any)
                                        }
                                    />
                                </div>
                            )}
                        </>
                    )}
            </div>
            <div className="flex-1 pr-2">
                {props.question.legacyCommunityInteraction !== InteractionType.none &&
                    isCustomizableSection(
                        Perspective.Community,
                        props.question.category as QuestionCategories,
                    ) && (
                        <>
                            <Input
                                value={props.prompt.legacyCommunityTitle}
                                onChange={props.updatePrompt(
                                    (prompt, val) =>
                                        (prompt.legacyCommunityTitle = val.target.value),
                                )}
                            />
                            {props.question.legacyCommunityAnnotation !==
                                AnnotationType.none && (
                                <div className="mt-1">
                                    <Checkbox
                                        label={additionalNotesLabel}
                                        checked={props.prompt.legacyCommunityAnnotation}
                                        onChange={e =>
                                            props.updatePrompt(
                                                (prompt, val) =>
                                                    (prompt.legacyCommunityAnnotation =
                                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                                                        val as any),
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                                            )(e.target.checked as any)
                                        }
                                    />
                                </div>
                            )}
                        </>
                    )}
            </div>
            <div className="flex-1 flex items-start gap-2">
                {props.moveUp ? (
                    <button
                        type="button"
                        data-action="moveup"
                        onClick={props.moveUp}
                        className="p-1 text-gray-800 rounded-full hover:bg-gray-100">
                        <ArrowUpIcon className="w-4 h-4" />
                    </button>
                ) : (
                    <div className="w-6 h-6" />
                )}
                {props.moveDown ? (
                    <button
                        type="button"
                        data-action="movedown"
                        onClick={props.moveDown}
                        className="p-1 text-gray-800 rounded-full hover:bg-gray-100">
                        <ArrowDownIcon className="w-4 h-4" />
                    </button>
                ) : (
                    <div className="w-6 h-6" />
                )}
                <DeleteButton
                    backgroundColor="bg-white"
                    confirm={{
                        title: "Delete this option?",
                        message:
                            "This will cause this option to disappear from client intakes and community data. You will lose any data associated with this option.",
                        confirmLabel: "Yes, remove",
                    }}
                    onClick={props.delete}
                />
            </div>
        </div>
    );
};

function buildUpdatePrompt(
    props: React.PropsWithChildren<Props>,
    p: IPrompt,
): PromptUpdateCallback {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    return (cb: (p: IPrompt, val: any) => void) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
        return props.updateQuestion((q: IQuestion, val: any) => {
            const foundPromptIndex = q.prompts.findIndex(needle => needle.id === p.id);
            if (foundPromptIndex >= 0) {
                const newPrompt = { ...p };
                cb(newPrompt, val);
                q.prompts[foundPromptIndex] = newPrompt;
            }
        });
    };
}
